/**
 * 租户级定制配置（按后端登录响应的 ui_profile 展示定制页面 / 注入额外菜单）。
 *
 * 本期定位（refactor 规范 19 的前身）：
 *   - 后端把 ui_profile 存在租户管理员上，普通子用户登录时继承该值。
 *   - ui_profile 只决定外观和定制菜单候选项；最终页面可见性仍由 page_keys 过滤，
 *     不会因为继承租户外观而扩大普通用户权限。
 *
 * 字段：
 *   overview     概况页变体 key，被 platOverviewDispatch.vue 用来决定渲染哪个概况组件。
 *                'default' 走通用 platOverview.vue。
 *   extraMenus   注入到侧边菜单的额外项，每项的落点三选一：
 *                  insertUnder: '<父级 path>'  挂到某个 submenu 的子项里
 *                  insertAfter: '<顶级 path>'  作为独立顶级项，排在该顶级项之后
 *                  （都不写）                    作为独立顶级项追加到菜单末尾
 *                顶级项需带 iconclass（菜单模板要渲染图标）。
 */
import { PAGE_PERMISSION } from '@/config/permissions.js'

export const OVERVIEW_VARIANT = Object.freeze({
  DEFAULT: 'default',
  DENTAL: 'dental', // 牙椅水路清洗消毒管理
})

export const UI_PROFILE = Object.freeze({
  DENTAL: 'dental',
})

export const CUSTOM_PROFILES = Object.freeze({
  // 牙科消毒租户；管理员与其普通子用户共用此外观。
  [UI_PROFILE.DENTAL]: {
    overview: OVERVIEW_VARIANT.DENTAL,
    extraMenus: [
      {
        // 独立顶级菜单，排在「质量控制」之后
        insertAfter: '/qualityControl',
        item: {
          path: '/disinfectionRecord',
          name: '消毒中心',
          iconclass: 'el-icon-document-checked',
          permission: PAGE_PERMISSION.QUALITY_DISINFECTION_RECORD,
        },
      },
    ],
  },
})

/** 按后端下发的 ui_profile 取定制配置，无定制返回 null。 */
export function getProfile(uiProfile) {
  const key = String(uiProfile || '').trim().toLowerCase()
  return key ? CUSTOM_PROFILES[key] || null : null
}

/** 概况页变体 key，未命中定制返回 'default'。 */
export function getOverviewVariant(uiProfile) {
  const profile = getProfile(uiProfile)
  return profile ? profile.overview : OVERVIEW_VARIANT.DEFAULT
}

/**
 * 把账号定制的额外菜单合并进基础 menuList，返回新数组（不改入参）。
 * homePage.vue 在权限过滤之前调用：先按租户 profile 加料，再统一按页面权限裁剪。
 */
export function applyCustomMenus(menuList, uiProfile) {
  const profile = getProfile(uiProfile)
  if (!profile || !profile.extraMenus || profile.extraMenus.length === 0) {
    return menuList
  }
  // 浅克隆顶层 + children，避免污染 homePage 里那份 menuList 单例
  const cloned = menuList.map((item) => ({
    ...item,
    children: item.children ? [...item.children] : undefined,
  }))
  profile.extraMenus.forEach((extra) => {
    // 按 path 去重（顶级或子级已存在都算存在），保证幂等
    const exists =
      cloned.some((m) => m.path === extra.item.path) ||
      cloned.some((m) => m.children && m.children.some((c) => c.path === extra.item.path))
    if (exists) return

    if (extra.insertUnder) {
      const parent = cloned.find((m) => m.path === extra.insertUnder)
      if (parent && parent.children) {
        parent.children = [...parent.children, extra.item]
      }
    } else if (extra.insertAfter) {
      const idx = cloned.findIndex((m) => m.path === extra.insertAfter)
      if (idx >= 0) cloned.splice(idx + 1, 0, { ...extra.item })
      else cloned.push({ ...extra.item })
    } else {
      cloned.push({ ...extra.item })
    }
  })
  return cloned
}
